'use client';
import React, { useState } from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { useApp } from '@/lib/AppContext';
import { t } from '@/lib/i18n';
import LevelCard from './LevelCard';
import LevelDetailPanel from './LevelDetailPanel';
import type { Level } from '@/lib/mockData';

export default function LearningPathContent() {
  const { language } = useLanguage();
  const { userLevels } = useApp();
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(userLevels[2] ?? userLevels[0]);

  const totalLessons = userLevels.reduce((acc, l) => acc + l.lessons.length, 0);
  const completedLessons = userLevels.reduce(
    (acc, l) => acc + l.lessons.filter((ls) => ls.status === 'completed').length,
    0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-700 text-foreground">{t(language, 'learning_title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t(language, 'learning_subtitle')}</p>
      </div>

      {/* Progress summary */}
      <div className="bg-card rounded-2xl border border-border card-shadow p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-600 text-foreground">
            {language === 'fr' ? 'Progression globale' : 'Общий прогресс'}
          </span>
          <span className="text-sm font-700 text-primary">
            {completedLessons}/{totalLessons} {language === 'fr' ? 'leçons' : 'уроков'}
          </span>
        </div>
        <div className="h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full gradient-primary rounded-full transition-all duration-700"
            style={{ width: `${Math.round((completedLessons / totalLessons) * 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">
            {Math.round((completedLessons / totalLessons) * 100)}%
          </span>
          <span className="text-xs text-muted-foreground">
            {userLevels.filter((l) => l.completionPercent === 100).length}/10{' '}
            {language === 'fr' ? 'niveaux complétés' : 'уровней завершено'}
          </span>
        </div>
      </div>

      {/* Main content: tree + detail */}
      <div className="flex gap-6">
        {/* Level tree */}
        <div className="flex-1 space-y-3">
          {userLevels.map((level, index) => (
            <div key={`level-node-${level.id}`} className="relative">
              {/* Connector line */}
              {index < userLevels.length - 1 && (
                <div className="absolute left-8 top-full w-0.5 h-3 bg-border z-10" />
              )}
              <LevelCard
                level={level}
                language={language}
                isSelected={selectedLevel?.id === level.id}
                onSelect={() => setSelectedLevel(level)}
              />
            </div>
          ))}
        </div>

        {/* Detail panel */}
        <div className="w-80 xl:w-96 flex-shrink-0">
          {selectedLevel && (
            <LevelDetailPanel level={selectedLevel} language={language} />
          )}
        </div>
      </div>
    </div>
  );
}